import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  FaSearch, FaChevronDown, FaChevronUp, FaPlus, FaEdit, FaTrash, 
  FaTimes, FaListUl, FaCalendarAlt, FaMoneyCheckAlt, FaLayerGroup, FaClock, FaUniversity, FaUsers, FaCopy
} from 'react-icons/fa';
import { useAuth } from '../../../context/AuthContext';
import Swal from 'sweetalert2';
import api from '../../../utils/api';
import useColleges from '../../../utils/useColleges';
import './FeeStructure.css';

const toTitleCase = (str) => (str || '').replace(/\w\S*/g, w => w.charAt(0).toUpperCase() + w.slice(1));

const FEE_HEADS = [
  'Tuition Fee', , 'Development Fee', 'Training Fee','Caution Money',
  'Library Fee','Exam Fee', 'Lab Fee',
  'Sports Fee', 'Admission Fee', 'Other'
];

const DEFAULT_PARTICULARS = [
  { head: 'Tuition Fee',  customName: '', amount: '', isCustom: false },
  { head: 'Training Fee', customName: '', amount: '', isCustom: false },
];

const EMPTY_FORM = {
  name:          '',
  collegeId:     '',
  collegeName:   '',
  sessionId:     '',
  selectedClass: '',  
  selectedBatch: '',  
  plan:          'Yearly',
  durationFrom:  '',
  durationTo:    '',
  paymentStart:  '',
  paymentEnd:    '',
  lateFeePerDay: '',
  particulars:   DEFAULT_PARTICULARS.map(p => ({ ...p })),
};

const FeeStructure = () => {
  const { user }  = useAuth();
  const isAdmin   = user?.role === 'admin' || (user?.colleges?.length > 0);
  const { colleges, loading: collegesLoading } = useColleges();

  const collegeDropRef = useRef(null);

  const [fees, setFees] = useState([]);
  const [loadingFees, setLoadingFees] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);
  
  const [previewStudents, setPreviewStudents] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  
  const [editPreviewStudents, setEditPreviewStudents] = useState([]);
  const [showEditPreview, setShowEditPreview] = useState(false);
  const [loadingEditPreview, setLoadingEditPreview] = useState(false);
  const [editPreviewMeta, setEditPreviewMeta] = useState(null);
  const [previewSessionActive, setPreviewSessionActive] = useState(true);
  
  // Store original single class/batch for tracking changes during edit
  const [originalClass, setOriginalClass] = useState('');
  const [originalBatch, setOriginalBatch] = useState('');

  const [searchQuery, setSearchQuery]     = useState('');
  const [filterCollege, setFilterCollege] = useState('');
  const [filterSession, setFilterSession] = useState('');
  const [filterBatch, setFilterBatch]     = useState('');

  const [activeSession, setActiveSession] = useState(null);
  const [classes,       setClasses]       = useState([]);
  const [batchYears,    setBatchYears]    = useState([]); 
  const [showCollegeDrop, setShowCollegeDrop] = useState(false);
  const [collegeSearch, setCollegeSearch] = useState('');
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isAdmin && colleges.length > 0) {
      setFilterCollege(String(colleges[0]._id));
      setFormData(prev => ({ ...prev, collegeId: String(colleges[0]._id), collegeName: colleges[0].name }));
    } else if (isAdmin && colleges.length === 1) {
      setFilterCollege(String(colleges[0]._id));
      setFormData(prev => ({ ...prev, collegeId: String(colleges[0]._id), collegeName: colleges[0].name }));
    }
  }, [isAdmin, colleges]);

  const loadFees = async () => {
    setLoadingFees(true);
    try {
      const res = await api.get('/fees', { params: { limit: 1000 } });
      setFees(res.data.data || []);
    } catch (err) {
      Swal.fire('Error', 'Failed to load fee structures.', 'error');
    } finally {
      setLoadingFees(false);
    }
  };

  useEffect(() => { loadFees(); }, []);

  useEffect(() => {
    if (!formData.collegeId) {
      setActiveSession(null); setClasses([]); setBatchYears([]);
      if(!editId) setFormData(prev => ({ ...prev, sessionId: '', selectedClass: '', selectedBatch: '' }));
      return;
    }

    // Fetch active session
    api.get('/sessions', { params: { college: formData.collegeId } })
      .then(res => {
        const data = res.data.data || [];
        const active = data.find(s => s.isActive) || null;
        setActiveSession(active);
        if (active && !editId) setFormData(prev => ({ ...prev, sessionId: active._id }));
        else if (data.length > 0 && editId) {
          const sess = data.find(s => s._id === formData.sessionId) || data[0];
          if (sess) setFormData(prev => ({ ...prev, sessionId: sess._id }));
        }
      }).catch(() => setActiveSession(null));

    // Fetch classes
    api.get('/classes', { params: { college: formData.collegeId } })
      .then(res => setClasses(res.data.data || []))
      .catch(() => setClasses([]));

    // Fetch batch years
    api.get('/students', { params: { college: formData.collegeId, limit: 1000 } })
      .then(res => {
        const students = res.data.data || [];
        const years = [...new Set(students.map(s => s.admissionYear).filter(Boolean))].sort();
        setBatchYears(years);
      })
      .catch(() => setBatchYears([]));
  }, [formData.collegeId, editId]);

  useEffect(() => {
    const h = (e) => {
      if (collegeDropRef.current && !collegeDropRef.current.contains(e.target)) setShowCollegeDrop(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const uniqueSessionsForFilter = useMemo(() => {
    const sMap = new Map();
    fees.forEach(f => { if (f.session) sMap.set(f.session._id, f.session.name); });
    return Array.from(sMap, ([id, name]) => ({ id, name }));
  }, [fees]);

  const uniqueBatchesForFilter = useMemo(() => {
    const bSet = new Set();
    fees.forEach(f => { if (f.batches) f.batches.forEach(b => bSet.add(b)); });
    return Array.from(bSet).sort();
  }, [fees]);

  const filteredFees = useMemo(() => {
    let result = fees;
    if (filterCollege) result = result.filter(f => String(f.college?._id || f.college) === filterCollege);
    if (filterSession) result = result.filter(f => String(f.session?._id || f.session) === filterSession);
    if (filterBatch)   result = result.filter(f => f.batches?.includes(filterBatch));
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(f => f.name.toLowerCase().includes(q));
    }
    return result;
  }, [fees, filterCollege, filterSession, filterBatch, searchQuery]);

  const groupedFees = useMemo(() => {
    const g = {};
    filteredFees.forEach(fee => {
      const collegeId = fee.college?._id || fee.college;
      const found = colleges.find(c => String(c._id) === String(collegeId));
      const key = found?.name || fee.college?.name || 'Unassigned';
      if (!g[key]) g[key] = [];
      g[key].push(fee);
    });
    return g;
  }, [filteredFees, colleges]);

  const resetForm = () => {
    setFormData(EMPTY_FORM);
    setEditId(null);
    setShowAddForm(false);
    setShowPreview(false);
    setPreviewStudents([]);
    setPreviewSessionActive(true);
    setShowEditPreview(false);
    setEditPreviewStudents([]);
    setEditPreviewMeta(null);
    setOriginalClass('');
    setOriginalBatch('');
    if (!isAdmin && colleges.length > 0) {
      setFormData(prev => ({ ...prev, collegeId: String(colleges[0]._id), collegeName: colleges[0].name }));
    }
  };

  const handleEditClick = (fee) => {
    setEditId(fee._id);
    const oClass = fee.classes?.[0]?._id || fee.classes?.[0] || '';
    const oBatch = fee.batches?.[0] || '';
    setOriginalClass(oClass);
    setOriginalBatch(oBatch);

    setFormData({
      name: fee.name,
      collegeId: fee.college?._id || fee.college || '',
      collegeName: fee.college?.name || '',
      sessionId: fee.session?._id || fee.session || '',
      selectedClass: oClass,
      selectedBatch: oBatch,
      plan: fee.plan || 'Yearly',
      durationFrom: fee.durationFrom ? fee.durationFrom.split('T')[0] : '',
      durationTo: fee.durationTo ? fee.durationTo.split('T')[0] : '',
      paymentStart: fee.paymentStart ? fee.paymentStart.split('T')[0] : '',
      paymentEnd: fee.paymentEnd ? fee.paymentEnd.split('T')[0] : '',
      lateFeePerDay: fee.lateFeePerDay || 0,
      particulars: fee.items?.length > 0 ? fee.items.map(i => {
        const isStandard = FEE_HEADS.includes(i.type);
        return { head: isStandard ? i.type : 'Other', customName: isStandard ? '' : i.type, amount: i.amount, isCustom: !isStandard };
      }) : DEFAULT_PARTICULARS.map(p => ({ ...p }))
    });
    setShowAddForm(true);
    setShowPreview(false);
    setPreviewStudents([]);
    setShowEditPreview(false);
    setEditPreviewStudents([]);
    setExpandedRow(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCopyClick = (fee) => {
    const oClass = fee.classes?.[0]?._id || fee.classes?.[0] || '';
    const oBatch = fee.batches?.[0] || '';
    
    setFormData({
      name: '',
      collegeId: fee.college?._id || fee.college || '',
      collegeName: fee.college?.name || '',
      sessionId: fee.session?._id || fee.session || '',
      selectedClass: oClass,
      selectedBatch: oBatch,
      plan: fee.plan || 'Yearly',
      durationFrom: fee.durationFrom ? fee.durationFrom.split('T')[0] : '',
      durationTo: fee.durationTo ? fee.durationTo.split('T')[0] : '',
      paymentStart: fee.paymentStart ? fee.paymentStart.split('T')[0] : '',
      paymentEnd: fee.paymentEnd ? fee.paymentEnd.split('T')[0] : '',
      lateFeePerDay: fee.lateFeePerDay || 0,
      particulars: fee.items?.length > 0 ? fee.items.map(i => {
        const isStandard = FEE_HEADS.includes(i.type);
        return { head: isStandard ? i.type : 'Other', customName: isStandard ? '' : i.type, amount: i.amount, isCustom: !isStandard };
      }) : DEFAULT_PARTICULARS.map(p => ({ ...p }))
    });
    
    setEditId(null); 
    setShowAddForm(true);
    setShowPreview(false);
    setPreviewStudents([]);
    setShowEditPreview(false);
    setEditPreviewStudents([]);
    setExpandedRow(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (feeId, name, currentIsActive) => {
    const result = await Swal.fire({
      title: 'Delete Fee Structure?',
      text: `Are you sure you want to delete "${name}"?`,
      icon: 'warning', showCancelButton: true,
      confirmButtonColor: '#e74c3c', confirmButtonText: 'Yes, delete it!'
    });
    if (!result.isConfirmed) return;
    try {
      await api.delete(`/fees/${feeId}`);
      setFees(prev => prev.filter(f => f._id !== feeId));
      Swal.fire('Deleted!', 'Fee structure removed.', 'success');
    } catch (err) {
      const data = err.response?.data;
      if (data?.isUsed) {
        const rows = (data.breakdown || []).map(r => `<tr><td style="padding:4px 10px;text-align:left">${r.cls}</td><td style="padding:4px 10px">Batch ${r.batch}</td><td style="padding:4px 10px;font-weight:700;color:#16a34a">${r.count} student${r.count !== 1 ? 's' : ''}</td></tr>`).join('');
        Swal.fire({
          title: 'Cannot Delete',
          html: `<b>${name}</b> is assigned to <b>${data.totalCount} student(s)</b>:<br/><br/>
            <table style="width:100%;border-collapse:collapse;font-size:0.88rem">
              <thead><tr style="background:#f1f5f9"><th style="padding:6px 10px">Class/Course</th><th style="padding:6px 10px">Batch</th><th style="padding:6px 10px">Students</th></tr></thead>
              <tbody>${rows}</tbody>
            </table><br/>
            <span style="font-size:0.82rem;color:#64748b">Deactivate instead — existing student fees are preserved, no new assignments.</span>`,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Deactivate Instead',
          cancelButtonText: 'Cancel',
          confirmButtonColor: '#f59e0b'
        }).then(r => { 
          // 🔒 FIX: Only deactivate if currently ACTIVE, don't toggle if already inactive
          if (r.isConfirmed && currentIsActive !== false) {
            handleToggleActive(feeId, true); // true = currently active, so deactivate it
          }
        });
      } else {
        Swal.fire('Error', data?.message || 'Failed to delete.', 'error');
      }
    }
  };

  const handleToggleActive = async (feeId, currentlyActive) => {
    const action = currentlyActive ? 'Deactivate' : 'Activate';
    const result = await Swal.fire({
      title: `${action} Fee Structure?`,
      text: currentlyActive
        ? 'Deactivated fees will not be assigned to new students. Existing student fees are not affected.'
        : 'This fee structure will be active and assigned to new matching students.',
      icon: 'question', showCancelButton: true,
      confirmButtonColor: currentlyActive ? '#f59e0b' : '#10b981',
      confirmButtonText: `Yes, ${action}`
    });
    if (!result.isConfirmed) return;
    try {
      await api.patch(`/fees/${feeId}/toggle-active`);
      setFees(prev => prev.map(f => f._id === feeId ? { ...f, isActive: !f.isActive } : f));
    } catch (err) {
      Swal.fire('Error', err.response?.data?.message || 'Failed.', 'error');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const filteredColleges = useMemo(() => {
    if (!collegeSearch) return colleges;
    const q = collegeSearch.toLowerCase();
    return colleges.filter(c => c.name.toLowerCase().includes(q));
  }, [colleges, collegeSearch]);

  const handleParticularChange = (index, field, value) => {
    const updated = [...formData.particulars];
    updated[index] = { ...updated[index], [field]: value };
    setFormData(prev => ({ ...prev, particulars: updated }));
  };

  const toggleCustom = (index) => {
    const updated = [...formData.particulars];
    updated[index] = { ...updated[index], isCustom: !updated[index].isCustom, customName: '' };
    setFormData(prev => ({ ...prev, particulars: updated }));
  };

  const addParticularRow = () => setFormData(prev => ({ ...prev, particulars: [...prev.particulars, { head: 'Other', customName: '', amount: '', isCustom: false }] }));
  const removeParticularRow = (index) => setFormData(prev => ({ ...prev, particulars: prev.particulars.filter((_, i) => i !== index) }));

  const totalAmount = useMemo(() => formData.particulars.reduce((acc, p) => acc + (parseFloat(p.amount) || 0), 0), [formData.particulars]);

  const fetchEditPreview = async () => {
    const classChanged = formData.selectedClass !== originalClass;
    const batchChanged = formData.selectedBatch !== originalBatch;
    const hasChange = classChanged || batchChanged;
    
    if (!hasChange) {
      setShowEditPreview(true);
      setEditPreviewStudents([]);
      setEditPreviewMeta({ noChange: true });
      return;
    }

    setLoadingEditPreview(true);
    try {
      const [stuRes, sessRes] = await Promise.all([
        api.get('/students', { params: { college: formData.collegeId, limit: 1000 } }),
        api.get('/sessions', { params: { college: formData.collegeId } })
      ]);
      const all = stuRes.data.data || [];
      const sessions = sessRes.data.data || [];
      const feeSession = sessions.find(s => String(s._id) === String(formData.sessionId));
      const sessionActive = feeSession?.isActive === true;

      const newStudents = all.filter(s => {
        const cls   = String(s.class?._id || s.class);
        const batch = String(s.admissionYear);
        const sess  = String(s.session?._id || s.session);
        return cls === formData.selectedClass && batch === formData.selectedBatch && sess === String(formData.sessionId);
      });
      
      setEditPreviewStudents(newStudents);
      setEditPreviewMeta({ sessionActive, feeSessionName: feeSession?.name || '', noChange: false });
      setShowEditPreview(true);
    } catch {
      Swal.fire('Error', 'Failed to load students.', 'error');
    } finally {
      setLoadingEditPreview(false);
    }
  };

  const fetchPreviewStudents = async () => {
    if (!formData.collegeId) return Swal.fire('Error', 'Please select a College.', 'warning');
    if (!formData.selectedClass) return Swal.fire('Error', 'Please select a Class/Course.', 'warning');
    if (!formData.selectedBatch) return Swal.fire('Error', 'Please select a Batch.', 'warning');
    if (totalAmount <= 0) return Swal.fire('Error', 'Total fee amount must be greater than zero.', 'warning');
    
    // Check for duplicate based on plan
    const existingFees = fees.filter(f => 
      String(f.college?._id || f.college) === formData.collegeId &&
      String(f.session?._id || f.session) === formData.sessionId &&
      f.classes?.some(c => String(c._id || c) === formData.selectedClass) &&
      f.batches?.includes(formData.selectedBatch) &&
      f.plan === formData.plan &&
      (!editId || String(f._id) !== String(editId))
    );
    
    const planLimits = { 'Yearly': 1, 'Half-Yearly': 2, 'Quarterly': 4 };
    const maxAllowed = planLimits[formData.plan] || 1;
    
    if (existingFees.length >= maxAllowed) {
      const planText = formData.plan === 'Yearly' ? 'Yearly (1 time only)' : 
                       formData.plan === 'Half-Yearly' ? 'Half-Yearly (2 times only)' : 
                       'Quarterly (4 times only)';
      return Swal.fire({
        icon: 'error',
        title: 'Duplicate Fee Structure',
        html: `A <b>${planText}</b> fee structure already exists for this <b>Class/Batch</b> combination.<br/><br/>` +
              `<b>Existing:</b> ${existingFees.map(f => f.name).join(', ')}<br/><br/>` +
              `Maximum ${maxAllowed} fee structure(s) allowed for ${formData.plan} plan.`,
        confirmButtonColor: '#e74c3c'
      });
    }
    
    setLoadingPreview(true);
    try {
      const [stuRes, sessRes] = await Promise.all([
        api.get('/students', { params: { college: formData.collegeId, limit: 1000 } }),
        api.get('/sessions', { params: { college: formData.collegeId } })
      ]);
      const all = stuRes.data.data || [];
      const sessions = sessRes.data.data || [];
      const feeSession = sessions.find(s => String(s._id) === String(formData.sessionId));
      const sessionActive = feeSession?.isActive === true;
      setPreviewSessionActive(sessionActive);
      
      const filtered = all.filter(s => {
        const inClass   = String(s.class?._id || s.class) === formData.selectedClass;
        const inBatch   = String(s.admissionYear) === formData.selectedBatch;
        const inSession = String(s.session?._id || s.session) === String(formData.sessionId);
        return inClass && inBatch && inSession;
      });
      setPreviewStudents(filtered);
      setShowPreview(true);
    } catch {
      Swal.fire('Error', 'Failed to load students.', 'error');
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!editId && !showPreview)    { fetchPreviewStudents(); return; }
    if (editId  && !showEditPreview){ fetchEditPreview();     return; }
    if (!formData.collegeId) return Swal.fire('Error', 'Please select a College.', 'warning');
    if (!formData.selectedClass) return Swal.fire('Error', 'Please select a Class/Course.', 'warning');
    if (!formData.selectedBatch) return Swal.fire('Error', 'Please select a Batch.', 'warning');
    if (totalAmount <= 0) return Swal.fire('Error', 'Total fee amount must be greater than zero.', 'warning');
    
    setSaving(true);
    try {
      // Backend perfectly maps array fields, wrapping our single selection in an array guarantees full compatibility.
      const payload = {
        name:          formData.name,
        college:       formData.collegeId,
        session:       formData.sessionId,
        classes:       [formData.selectedClass],
        batches:       [formData.selectedBatch],
        plan:          formData.plan,
        durationFrom:  formData.durationFrom,
        durationTo:    formData.durationTo,
        paymentStart:  formData.paymentStart,
        paymentEnd:    formData.paymentEnd,
        lateFeePerDay: parseFloat(formData.lateFeePerDay) || 0,
        totalAmount,
        items: formData.particulars.map(p => ({
          type:   p.isCustom ? (p.customName.trim() || 'Other') : p.head,
          amount: parseFloat(p.amount) || 0,
        })),
      };
      if (editId) {
        await api.put(`/fees/${editId}`, payload);
        Swal.fire('Updated!', 'Fee Structure updated. Changes apply to matching students.', 'success');
      } else {
        await api.post('/fees', payload);
        Swal.fire('Created!', `Fee Structure saved and assigned to ${previewStudents.length} matching student(s).`, 'success');
      }
      resetForm();
      loadFees();
    } catch (error) {
      Swal.fire('Error', error.response?.data?.message || 'Failed to save Fee Structure.', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="student-fee-structure-container">
      <h2 className="student-fee-structure-title">Fee Structures</h2>

      <div className="student-fee-structure-controls">
        <div className="student-fee-structure-search-wrapper">
          <FaSearch className="student-fee-structure-search-icon" />
          <input 
            type="text" 
            className="student-fee-structure-search-input" 
            placeholder="Search fee name..." 
            value={searchQuery} 
            onChange={e => setSearchQuery(e.target.value)} 
          />
          {searchQuery && <button className="student-fee-structure-search-clear" onClick={() => setSearchQuery('')}><FaTimes /></button>}
        </div>

        {isAdmin && (
          <select className="student-fee-structure-filter-select" value={filterCollege} onChange={e => setFilterCollege(e.target.value)}>
            <option value="">All Colleges</option>
            {colleges.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
          </select>
        )}

        <select className="student-fee-structure-filter-select" value={filterSession} onChange={e => setFilterSession(e.target.value)}>
          <option value="">All Sessions</option>
          {uniqueSessionsForFilter.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>

        <select className="student-fee-structure-filter-select" value={filterBatch} onChange={e => setFilterBatch(e.target.value)}>
          <option value="">All Batches</option>
          {uniqueBatchesForFilter.map(b => <option key={b} value={b}>{b}</option>)}
        </select>

        <button className="student-fee-structure-btn student-fee-structure-btn-add" onClick={() => { if (showAddForm && !editId) resetForm(); else { resetForm(); setShowAddForm(true); }}}>
          {showAddForm && !editId ? <><FaTimes /> Close Form</> : <><FaPlus /> Add Fee Structure</>}
        </button>
      </div>

      {showAddForm && (
        <form className="student-fee-structure-inline-form" onSubmit={handleSubmit}>
          <h3 className="student-fee-structure-form-title">{editId ? '✏️ Edit Fee Structure' : '➕ Create New Fee Structure'}</h3>

          <h4 className="student-fee-structure-section-title"><div style={{display:'flex', alignItems:'center', gap:'8px'}}><FaLayerGroup/> Basic Details</div></h4>
          <div className="student-fee-structure-form-grid">
            <div className="student-fee-structure-form-group student-fee-structure-col-span-2">
              <label>Fee Structure Name <span className="student-fee-structure-req">*</span></label>
              <input type="text" name="name" className="student-fee-structure-input-field" value={formData.name} onChange={handleChange} placeholder="e.g. B.Tech 1st Year Fee 2025" required />
            </div>

            {isAdmin && (
              <div className="student-fee-structure-form-group" ref={collegeDropRef}>
                <label>College <span className="student-fee-structure-req">*</span></label>
                {editId ? (
                  <input type="text" className="student-fee-structure-input-field" value={formData.collegeName} readOnly disabled style={{background:'#f1f5f9',cursor:'not-allowed'}} />
                ) : (
                  <>
                    <div className="student-fee-structure-select-box" onClick={() => setShowCollegeDrop(!showCollegeDrop)}>
                      <span className="student-fee-structure-select-text" style={{ color: formData.collegeName ? '#2c3e50' : '#6c757d' }}>
                        {formData.collegeName || '-- Select College --'}
                      </span>
                      <FaChevronDown style={{color: '#6c757d', fontSize: '0.85rem'}} />
                    </div>
                    {showCollegeDrop && (
                      <div className="student-fee-structure-dropdown-menu">
                        <div className="student-fee-structure-dropdown-search-bar">
                          <FaSearch style={{color: '#94a3b8', fontSize: '0.85rem'}} />
                          <input type="text" placeholder="Search college..." value={collegeSearch} onChange={e => setCollegeSearch(e.target.value)} autoFocus />
                        </div>
                        <div className="student-fee-structure-options-list">
                          {filteredColleges.map(c => (
                            <div key={c._id} className={`student-fee-structure-option-item ${formData.collegeId === c._id ? 'selected' : ''}`}
                              onClick={() => { setFormData(prev => ({ ...prev, collegeId: c._id, collegeName: c.name })); setShowCollegeDrop(false); setCollegeSearch(''); }}>
                              <span>{c.name}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            <div className="student-fee-structure-form-group">
              <label>Session <span className="student-fee-structure-req">*</span></label>
              {!formData.collegeId ? (
                <input type="text" className="student-fee-structure-input-field student-fee-structure-input-readonly" value="Select college first" readOnly disabled />
              ) : activeSession ? (
                <input type="text" className="student-fee-structure-input-field student-fee-structure-input-active" value={activeSession.name} readOnly disabled />
              ) : editId ? (
                <input type="text" className="student-fee-structure-input-field student-fee-structure-input-active" value={fees.find(f => f._id === editId)?.session?.name || ''} readOnly disabled />
              ) : (
                <input type="text" className="student-fee-structure-input-field student-fee-structure-input-error" value="No active session" readOnly disabled />
              )}
            </div>

            <div className="student-fee-structure-form-group">
              <label>Fee Plan <span className="student-fee-structure-req">*</span></label>
              <select name="plan" className="student-fee-structure-input-field" value={formData.plan} onChange={handleChange} required>
                <option value="Yearly">Yearly</option>
                <option value="Half-Yearly">Half-Yearly</option>
                <option value="Quarterly">Quarterly</option>
              </select>
            </div>

            <div className="student-fee-structure-form-group">
              <label>Target Class/Course <span className="student-fee-structure-req">*</span></label>
              <select 
                name="selectedClass" 
                className="student-fee-structure-input-field" 
                value={formData.selectedClass} 
                onChange={handleChange} 
                required
              >
                <option value="">-- Select Class --</option>
                {classes.map(c => <option key={c._id} value={c._id}>{c.name}{c.medium ? ` (${c.medium})` : ''}</option>)}
              </select>
            </div>

            <div className="student-fee-structure-form-group">
              <label>Target Batch <span className="student-fee-structure-req">*</span></label>
              <select 
                name="selectedBatch" 
                className="student-fee-structure-input-field" 
                value={formData.selectedBatch} 
                onChange={handleChange} 
                required
              >
                <option value="">-- Select Batch --</option>
                {batchYears.map(yr => <option key={yr} value={yr}>{yr}</option>)}
              </select>
            </div>
          </div>

          <h4 className="student-fee-structure-section-title">
            <div style={{display:'flex', alignItems:'center', gap:'8px'}}><FaCalendarAlt/> Duration & Deadlines</div>
          </h4>

          <div className="student-fee-structure-date-group-container">
            <div className="student-fee-structure-date-card">
              <h5 className="student-fee-structure-date-card-title"><FaUniversity color="#3498db" /> Academic Coverage Timeline</h5>
              <p className="student-fee-structure-date-card-desc">Define the specific duration or academic timeline this fee covers for the college.</p>
              
              <div className="student-fee-structure-date-grid">
                <div className="student-fee-structure-form-group">
                  <label>Coverage From <span className="student-fee-structure-req">*</span></label>
                  <input type="date" name="durationFrom" className="student-fee-structure-input-field" value={formData.durationFrom} onChange={handleChange} required />
                </div>
                <div className="student-fee-structure-form-group">
                  <label>Coverage To <span className="student-fee-structure-req">*</span></label>
                  <input type="date" name="durationTo" className="student-fee-structure-input-field" value={formData.durationTo} onChange={handleChange} required />
                </div>
              </div>
            </div>

            <div className="student-fee-structure-date-card">
              <h5 className="student-fee-structure-date-card-title"><FaClock color="#e67e22" /> Student Payment Window</h5>
              <p className="student-fee-structure-date-card-desc">Specify the due date for students to pay this fee, and any late penalty.</p>
              
              <div className="student-fee-structure-date-grid">
                <div className="student-fee-structure-form-group">
                  <label>Payment Due Date <span className="student-fee-structure-req">*</span></label>
                  <input type="date" name="paymentEnd" className="student-fee-structure-input-field" value={formData.paymentEnd} onChange={handleChange} required />
                </div>
                <div className="student-fee-structure-form-group">
                  <label>Late Fee / Day (₹)</label>
                  <input type="number" name="lateFeePerDay" className="student-fee-structure-input-field" value={formData.lateFeePerDay} onChange={handleChange} placeholder="e.g. 50" min="0" />
                </div>
              </div>
            </div>
          </div>

          <h4 className="student-fee-structure-section-title">
            <div style={{display:'flex', alignItems:'center', gap:'8px'}}><FaMoneyCheckAlt/> Fee Breakdown</div>
            <button type="button" className="student-fee-structure-add-row-btn" onClick={addParticularRow}><FaPlus /> Add Row</button>
          </h4>
          
          <div className="student-fee-structure-particulars-list">
            {formData.particulars.map((item, index) => (
              <div key={index} className="student-fee-structure-particular-row">
                <div className="student-fee-structure-custom-wrap">
                  {item.isCustom ? (
                    <>
                      <input type="text" className="student-fee-structure-input-field" value={item.customName} onChange={e => handleParticularChange(index, 'customName', e.target.value)} placeholder="Custom Fee Name" autoFocus />
                      <button type="button" className="student-fee-structure-toggle-btn" title="Switch to predefined list" onClick={() => toggleCustom(index)}>↩</button>
                    </>
                  ) : (
                    <>
                      <select className="student-fee-structure-input-field" value={item.head} onChange={e => handleParticularChange(index, 'head', e.target.value)}>
                        {FEE_HEADS.map(h => <option key={h} value={h}>{h}</option>)}
                      </select>
                      <button type="button" className="student-fee-structure-toggle-btn" title="Enter custom name" onClick={() => toggleCustom(index)}>✎</button>
                    </>
                  )}
                </div>
                <div>
                  <input type="number" className="student-fee-structure-input-field" value={item.amount} onChange={e => handleParticularChange(index, 'amount', e.target.value)} placeholder="Amount (₹)" min="0" required />
                </div>
                {formData.particulars.length > 1 ? (
                  <button type="button" className="student-fee-structure-btn-remove" onClick={() => removeParticularRow(index)}><FaTrash /></button>
                ) : <div style={{width: '42px'}}></div>}
              </div>
            ))}
          </div>
          
          <div className="student-fee-structure-total-bar">
            Total Fee Amount : <span>₹ {totalAmount.toLocaleString()}</span>
          </div>

          {showPreview && !editId && (() => {
            const summary = {};
            previewStudents.forEach(s => {
              const cls   = s.class?.name || s.class || 'Unknown';
              const batch = s.admissionYear || 'Unknown';
              const key   = `${cls}||${batch}`;
              if (!summary[key]) summary[key] = { cls, batch, count: 0 };
              summary[key].count++;
            });
            const summaryRows = Object.values(summary).sort((a,b) => a.cls.localeCompare(b.cls) || a.batch.localeCompare(b.batch));
            const sessionName = fees.find(f => String(f.session?._id || f.session) === formData.sessionId)?.session?.name || activeSession?.name || '';
            return (
              <div style={{margin:'16px 0', border:'1px solid #bae6fd', borderRadius:'8px', background:'#f0f9ff', padding:'20px'}}>
                <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'12px'}}>
                  <h4 style={{margin:0, color:'#0369a1', display:'flex', alignItems:'center', gap:'8px', fontSize:'1rem'}}>
                    <FaUsers /> Student Preview
                  </h4>
                  <span style={{background: previewSessionActive ? '#0369a1' : '#d97706', color:'#fff', borderRadius:'20px', padding:'3px 12px', fontSize:'0.82rem', fontWeight:700}}>
                    {previewStudents.length} student{previewStudents.length !== 1 ? 's' : ''} will be assigned
                  </span>
                </div>

                <div style={{background:'#e0f2fe', borderRadius:'6px', padding:'10px 14px', marginBottom:'12px', fontSize:'0.82rem', color:'#0369a1', display:'flex', flexWrap:'wrap', gap:'16px'}}>
                  <span><b>Fee:</b> {toTitleCase(formData.name)}</span>
                  <span><b>Session:</b> {sessionName || '—'}</span>
                  <span><b>Total:</b> ₹{totalAmount.toLocaleString()}</span>
                </div>

                {!previewSessionActive && (
                  <div style={{background:'#fef3c7', border:'1px solid #fde68a', borderRadius:'6px', padding:'10px 14px', marginBottom:'12px', fontSize:'0.82rem', color:'#92400e'}}>
                    ⚠️ <b>Session "{sessionName}" is not active yet.</b> Fee structure will be saved but <b>0 students will be assigned now</b>. Students will be assigned automatically when this session becomes active.
                  </div>
                )}

                {previewStudents.length === 0 && previewSessionActive && (
                  <div style={{background:'#fef2f2', border:'1px solid #fecaca', borderRadius:'6px', padding:'10px 14px', marginBottom:'12px', fontSize:'0.82rem', color:'#991b1b'}}>
                    ℹ️ No students found matching the selected class &amp; batch in session <b>{sessionName}</b>. Fee structure will be saved. Students added later with matching details will be assigned automatically.
                  </div>
                )}

                {previewStudents.length > 0 && (
                  <>
                    <div style={{marginBottom:'12px'}}>
                      <div style={{display:'flex', flexWrap:'wrap', gap:'8px'}}>
                        {summaryRows.map((r, i) => (
                          <div key={i} style={{background:'#fff', border:'1px solid #bae6fd', borderRadius:'6px', padding:'7px 12px', fontSize:'0.82rem'}}>
                            <span style={{fontWeight:700, color:'#0369a1'}}>{r.cls}</span>
                            <span style={{color:'#64748b', margin:'0 5px'}}>|</span>
                            <span style={{color:'#d97706', fontWeight:600}}>Batch {r.batch}</span>
                            <span style={{color:'#64748b', margin:'0 5px'}}>|</span>
                            <span style={{fontWeight:700, color:'#16a34a'}}>{r.count} student{r.count !== 1 ? 's' : ''}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div style={{maxHeight:'200px', overflowY:'auto', border:'1px solid #bae6fd', borderRadius:'6px'}}>
                      <table style={{width:'100%', borderCollapse:'collapse', fontSize:'0.8rem'}}>
                        <thead>
                          <tr style={{background:'#0369a1', position:'sticky', top:0}}>
                            {['#','Name','Adm No','Class/Course','Batch'].map(h => <th key={h} style={{padding:'7px 10px', textAlign:'left', color:'#fff', fontWeight:700}}>{h}</th>)}
                          </tr>
                        </thead>
                        <tbody>
                          {previewStudents.map((s, i) => (
                            <tr key={s._id} style={{background: i%2===0?'#fff':'#f0f9ff'}}>
                              <td style={{padding:'5px 10px', color:'#64748b'}}>{i+1}</td>
                              <td style={{padding:'5px 10px', fontWeight:600}}>{s.name}</td>
                              <td style={{padding:'5px 10px', color:'#64748b'}}>{s.admissionNo || '—'}</td>
                              <td style={{padding:'5px 10px'}}>{s.class?.name || s.class || '—'}</td>
                              <td style={{padding:'5px 10px', color:'#d97706', fontWeight:600}}>{s.admissionYear || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            );
          })()}

          {showEditPreview && editId && (() => {
            const meta = editPreviewMeta || {};
            if (meta.noChange) return (
              <div style={{margin:'12px 0', background:'#f0fdf4', border:'1px solid #bbf7d0', borderRadius:'6px', padding:'12px 16px', fontSize:'0.85rem', color:'#166534'}}>
                ✅ Target Class/Batch remains unchanged. Click <b>Confirm &amp; Update</b> to save the changes to details.
              </div>
            );
            const summary = {};
            editPreviewStudents.forEach(s => {
              const cls = s.class?.name || s.class || 'Unknown';
              const batch = s.admissionYear || 'Unknown';
              const key = `${cls}||${batch}`;
              if (!summary[key]) summary[key] = { cls, batch, count: 0 };
              summary[key].count++;
            });
            const summaryRows = Object.values(summary).sort((a,b) => a.cls.localeCompare(b.cls));
            return (
              <div style={{margin:'16px 0', border:'1px solid #fde68a', borderRadius:'8px', background:'#fffbeb', padding:'20px'}}>
                <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'12px'}}>
                  <h4 style={{margin:0, color:'#92400e', display:'flex', alignItems:'center', gap:'8px', fontSize:'1rem'}}>
                    <FaUsers /> Update Preview — Assignment Changes
                  </h4>
                  <span style={{background: meta.sessionActive ? '#d97706' : '#dc2626', color:'#fff', borderRadius:'20px', padding:'3px 12px', fontSize:'0.82rem', fontWeight:700}}>
                    {editPreviewStudents.length} student{editPreviewStudents.length !== 1 ? 's' : ''}
                  </span>
                </div>
                {!meta.sessionActive && (
                  <div style={{background:'#fef2f2', border:'1px solid #fecaca', borderRadius:'6px', padding:'10px 14px', marginBottom:'12px', fontSize:'0.82rem', color:'#991b1b'}}>
                    ⚠️ Session <b>"{meta.feeSessionName}"</b> is not active. <b>0 students will be assigned now.</b> Will auto-assign when session becomes active.
                  </div>
                )}
                {editPreviewStudents.length === 0 && meta.sessionActive && (
                  <p style={{color:'#64748b', fontSize:'0.85rem', margin:0}}>No students currently match the newly updated class/batch details.</p>
                )}
                {editPreviewStudents.length > 0 && (
                  <>
                    <div style={{display:'flex', flexWrap:'wrap', gap:'8px', marginBottom:'12px'}}>
                      {summaryRows.map((r, i) => (
                        <div key={i} style={{background:'#fff', border:'1px solid #fde68a', borderRadius:'6px', padding:'6px 12px', fontSize:'0.82rem'}}>
                          <span style={{fontWeight:700, color:'#92400e'}}>{r.cls}</span>
                          <span style={{color:'#64748b', margin:'0 5px'}}>|</span>
                          <span style={{color:'#d97706', fontWeight:600}}>Batch {r.batch}</span>
                          <span style={{color:'#64748b', margin:'0 5px'}}>|</span>
                          <span style={{fontWeight:700, color:'#16a34a'}}>{r.count} student{r.count !== 1 ? 's' : ''}</span>
                        </div>
                      ))}
                    </div>
                    <div style={{maxHeight:'180px', overflowY:'auto', border:'1px solid #fde68a', borderRadius:'6px'}}>
                      <table style={{width:'100%', borderCollapse:'collapse', fontSize:'0.8rem'}}>
                        <thead>
                          <tr style={{background:'#d97706', position:'sticky', top:0}}>
                            {['#','Name','Adm No','Class/Course','Batch'].map(h => <th key={h} style={{padding:'7px 10px', textAlign:'left', color:'#fff', fontWeight:700}}>{h}</th>)}
                          </tr>
                        </thead>
                        <tbody>
                          {editPreviewStudents.map((s, i) => (
                            <tr key={s._id} style={{background: i%2===0?'#fff':'#fffbeb'}}>
                              <td style={{padding:'5px 10px', color:'#64748b'}}>{i+1}</td>
                              <td style={{padding:'5px 10px', fontWeight:600}}>{s.name}</td>
                              <td style={{padding:'5px 10px', color:'#64748b'}}>{s.admissionNo || '—'}</td>
                              <td style={{padding:'5px 10px'}}>{s.class?.name || s.class || '—'}</td>
                              <td style={{padding:'5px 10px', color:'#d97706', fontWeight:600}}>{s.admissionYear || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            );
          })()}

          <div className="student-fee-structure-form-actions">
            {showPreview && !editId ? (
              <button type="button" className="student-fee-structure-btn-cancel" onClick={() => setShowPreview(false)} disabled={saving}>&#8592; Back</button>
            ) : showEditPreview && editId ? (
              <button type="button" className="student-fee-structure-btn-cancel" onClick={() => setShowEditPreview(false)} disabled={saving}>&#8592; Back</button>
            ) : (
              <button type="button" className="student-fee-structure-btn-cancel" onClick={resetForm} disabled={saving}>Cancel</button>
            )}
            <button type="submit" className="student-fee-structure-btn-confirm" disabled={saving || loadingPreview || loadingEditPreview}>
              {saving ? 'Saving...' : (loadingPreview || loadingEditPreview) ? 'Loading...' : editId ? (showEditPreview ? 'Confirm & Update' : 'Preview Changes') : (showPreview ? 'Confirm & Save' : 'Preview Students')}
            </button>
          </div>
        </form>
      )}

      {loadingFees ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>Loading structures...</div>
      ) : Object.keys(groupedFees).length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#64748b', background: '#fff', borderRadius: '8px' }}>No fee structures found.</div>
      ) : (
        <div className="student-fee-structure-grouped-container">
          {Object.entries(groupedFees).map(([collegeName, collegeFees]) => (
            <div key={collegeName} className="student-fee-structure-table-group">
              <h3 className="student-fee-structure-group-header">🏫 {collegeName}</h3>
              <div className="student-fee-structure-table-wrapper">
                <table className="student-fee-structure-table">
                  <thead>
                    <tr>
                      <th>S.No</th>
                      <th className="student-fee-structure-align-left">Fee Name</th>
                      <th className="student-fee-structure-col-session">Session</th>
                      <th className="student-fee-structure-col-batch">Batch</th>
                      <th className="student-fee-structure-col-class">Class/Course</th>
                      <th className="student-fee-structure-col-medium">Medium</th>
                      <th className="student-fee-structure-col-plan">Plan</th>
                      <th className="student-fee-structure-col-amount">Total Fee</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {collegeFees.map((fee, i) => (
                      <React.Fragment key={fee._id}>
                        <tr className="student-fee-structure-main-row">
                          <td>{i + 1}</td>
                          <td className="student-fee-structure-align-left">
                            <div className="student-fee-structure-name-wrapper" onClick={() => setExpandedRow(expandedRow === fee._id ? null : fee._id)}>
                              <span style={{ fontWeight: 600 }}>{toTitleCase(fee.name)}</span>
                              <button className="student-fee-structure-expand-btn">
                                {expandedRow === fee._id ? <FaChevronUp /> : <FaChevronDown />}
                              </button>
                            </div>
                          </td>
                          <td className="student-fee-structure-col-session">
                            <span className="student-fee-structure-badge-session">{fee.session?.name || '—'}</span>
                          </td>
                          <td className="student-fee-structure-col-batch">
                            <span className="student-fee-structure-badge-batch">{fee.batches?.join(', ') || '—'}</span>
                          </td>
                          <td className="student-fee-structure-col-class">
                            <span className="student-fee-structure-badge-session" style={{background: '#e3f2fd', color: '#1976d2', borderColor:'#bae6fd'}}>
                              {fee.classes?.map(c => c.name).join(', ') || '—'}
                            </span>
                          </td>
                          <td className="student-fee-structure-col-medium">
                            {(() => {
                              const mediums = fee.classes?.map(c => c.medium).filter(Boolean) || [];
                              const uniqueMediums = [...new Set(mediums)];
                              if (uniqueMediums.length === 0) return <span style={{color: '#94a3b8', fontSize: '0.8rem'}}>—</span>;
                              return uniqueMediums.map((m, idx) => {
                                const colors = {
                                  'English': { bg: '#e8f5e9', color: '#2e7d32', border: '#a5d6a7' },
                                  'Hindi': { bg: '#fff3e0', color: '#e65100', border: '#ffcc80' }
                                };
                                const style = colors[m] || { bg: '#f5f5f5', color: '#616161', border: '#e0e0e0' };
                                return (
                                  <span key={idx} className="student-fee-structure-badge-medium" 
                                    style={{background: style.bg, color: style.color, border: `1px solid ${style.border}`, padding: '5px 10px', borderRadius: '6px', fontSize: '0.8rem', fontWeight: 600, whiteSpace: 'nowrap', marginRight: idx < uniqueMediums.length - 1 ? '4px' : '0'}}>
                                    {m}
                                  </span>
                                );
                              });
                            })()}
                          </td>
                          <td className="student-fee-structure-col-plan">
                            <span className="student-fee-structure-badge-plan">{fee.plan || '—'}</span>
                          </td>
                          <td className="student-fee-structure-col-amount"><span className="student-fee-structure-amount-text">₹ {(fee.totalAmount || 0).toLocaleString()}</span></td>
                          <td>
                            <label className="fs-toggle-switch">
                              <input type="checkbox" checked={fee.isActive !== false} onChange={() => handleToggleActive(fee._id, fee.isActive !== false)} />
                              <span className="fs-toggle-slider" />
                            </label>
                            <div style={{fontSize:'0.7rem', marginTop:'4px', color: fee.isActive !== false ? '#27ae60' : '#e74c3c', fontWeight:700, textAlign:'center'}}>
                              {fee.isActive !== false ? 'Active' : 'Inactive'}
                            </div>
                          </td>
                          <td>
                            <div className="student-fee-structure-action-btns">
                              <button className="student-fee-structure-btn-edit" title="Edit" onClick={() => handleEditClick(fee)}><FaEdit /></button>
                              <button className="student-fee-structure-btn-copy" title="Copy / Duplicate" onClick={() => handleCopyClick(fee)}><FaCopy /></button>
                              <button className="student-fee-structure-btn-delete" title="Delete" onClick={() => handleDelete(fee._id, fee.name, fee.isActive)}><FaTrash /></button>
                            </div>
                          </td>
                        </tr>

                        {expandedRow === fee._id && (
                          <tr className="student-fee-structure-expanded-row">
                            <td colSpan="10">
                              <div className="student-fee-structure-expanded-content">
                                
                                <div className="student-fee-structure-expanded-grid">
                                  <div className="student-fee-structure-exp-item student-fee-structure-exp-mobile-only student-fee-structure-exp-session">
                                    <span className="student-fee-structure-exp-label">Session</span>
                                    <span className="student-fee-structure-exp-value">{fee.session?.name || '—'}</span>
                                  </div>
                                  <div className="student-fee-structure-exp-item student-fee-structure-exp-mobile-only student-fee-structure-exp-batch">
                                    <span className="student-fee-structure-exp-label">Batch</span>
                                    <span className="student-fee-structure-exp-value">{fee.batches?.join(', ') || '—'}</span>
                                  </div>
                                  <div className="student-fee-structure-exp-item student-fee-structure-exp-mobile-only student-fee-structure-exp-class">
                                    <span className="student-fee-structure-exp-label">Class / Course</span>
                                    <span className="student-fee-structure-exp-value">{fee.classes?.map(c => c.name).join(', ') || '—'}</span>
                                  </div>
                                  <div className="student-fee-structure-exp-item student-fee-structure-exp-mobile-only student-fee-structure-exp-medium">
                                    <span className="student-fee-structure-exp-label">Medium</span>
                                    <span className="student-fee-structure-exp-value">
                                      {(() => {
                                        const mediums = fee.classes?.map(c => c.medium).filter(Boolean) || [];
                                        const uniqueMediums = [...new Set(mediums)];
                                        if (uniqueMediums.length === 0) return '—';
                                        return uniqueMediums.map((m, idx) => {
                                          const colors = {
                                            'English': { bg: '#e8f5e9', color: '#2e7d32', border: '#a5d6a7' },
                                            'Hindi': { bg: '#fff3e0', color: '#e65100', border: '#ffcc80' }
                                          };
                                          const style = colors[m] || { bg: '#f5f5f5', color: '#616161', border: '#e0e0e0' };
                                          return (
                                            <span key={idx} className="student-fee-structure-badge-medium" 
                                              style={{background: style.bg, color: style.color, border: `1px solid ${style.border}`, padding: '5px 10px', borderRadius: '6px', fontSize: '0.8rem', fontWeight: 600, whiteSpace: 'nowrap', marginRight: idx < uniqueMediums.length - 1 ? '4px' : '0'}}>
                                              {m}
                                            </span>
                                          );
                                        });
                                      })()}
                                    </span>
                                  </div>
                                  <div className="student-fee-structure-exp-item student-fee-structure-exp-mobile-only student-fee-structure-exp-plan">
                                    <span className="student-fee-structure-exp-label">Plan</span>
                                    <span className="student-fee-structure-exp-value">{fee.plan || '—'}</span>
                                  </div>
                                  <div className="student-fee-structure-exp-item student-fee-structure-exp-mobile-only student-fee-structure-exp-amount">
                                    <span className="student-fee-structure-exp-label">Total Fee</span>
                                    <span className="student-fee-structure-exp-value student-fee-structure-amount-text">₹ {(fee.totalAmount || 0).toLocaleString()}</span>
                                  </div>

                                  <div className="student-fee-structure-exp-item">
                                    <span className="student-fee-structure-exp-label"><FaLayerGroup/> Fee Plan</span>
                                    <span className="student-fee-structure-exp-value">{fee.plan || '—'}</span>
                                  </div>
                                  <div className="student-fee-structure-exp-item">
                                    <span className="student-fee-structure-exp-label"><FaUniversity/> Coverage Duration</span>
                                    <span className="student-fee-structure-exp-value">{fee.durationFrom ? new Date(fee.durationFrom).toLocaleDateString('en-GB') : '—'} to {fee.durationTo ? new Date(fee.durationTo).toLocaleDateString('en-GB') : '—'}</span>
                                  </div>
                                  <div className="student-fee-structure-exp-item">
                                    <span className="student-fee-structure-exp-label"><FaClock/> Payment Due Date</span>
                                    <span className="student-fee-structure-exp-value">{fee.paymentEnd ? new Date(fee.paymentEnd).toLocaleDateString('en-GB') : '—'}</span>
                                  </div>
                                  <div className="student-fee-structure-exp-item">
                                    <span className="student-fee-structure-exp-label"><FaMoneyCheckAlt/> Late Fee / Day</span>
                                    <span className="student-fee-structure-exp-value" style={{color: '#e74c3c'}}>₹ {fee.lateFeePerDay || 0}</span>
                                  </div>
                                </div>

                                <div className="student-fee-structure-exp-particulars-box">
                                  <h4 className="student-fee-structure-exp-part-title"><FaListUl style={{marginRight: 8}}/> Fee Breakdown</h4>
                                  <table className="student-fee-structure-exp-part-table">
                                    <thead><tr><th>Particular</th><th style={{textAlign:'right'}}>Amount (₹)</th></tr></thead>
                                    <tbody>
                                      {(fee.items || []).map((itm, idx) => (
                                        <tr key={idx}>
                                          <td style={{color:'#1e293b'}}>{toTitleCase(itm.type) || '—'}</td>
                                          <td style={{textAlign:'right', fontWeight:600, color:'#1e293b'}}>₹ {(itm.amount || 0).toLocaleString()}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                    <tfoot>
                                      <tr><td>Total</td><td style={{textAlign:'right'}}>₹ {(fee.totalAmount || 0).toLocaleString()}</td></tr>
                                    </tfoot>
                                  </table>
                                </div>

                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}

    </div>
  );
};

export default FeeStructure;